/** The part of a step's fieldConfig these settings read: each field's saved required flag. */
export type RequiredSettings = Record<string, { isRequired?: boolean } | undefined> | undefined;

/**
 * The "How did you hear about us?" step's admin-configurable required settings, kept in the step's
 * generic fieldConfig blob.
 *
 * A follow-up is required per lead source, so its key is that option's catalogue key. A borrower is
 * only held to the setting of the lead source they actually picked; lead sources that ask nothing
 * (Radio, Letter in the Mail, Returning Client) have no key.
 *
 * Defaults are today's behaviour, so a saved flow changes nothing until an admin flips a switch: the
 * question and every free-text follow-up are required, and the agent list is not.
 */
export class LeadSourceFields {
  static readonly ANSWER = 'leadSource.howDidYouHear';

  /** The "Who was the agent" list, shown when the admin picks agent referrals from a list. */
  static readonly AGENT_FROM_LIST = 'leadSource.agentFromList';

  private static readonly FOLLOW_UP_BY_LEAD_SOURCE: Record<string, string> = {
    'Social Media': 'leadSource.socialMedia',
    'Real Estate Agent': 'leadSource.realEstateAgent',
    // Retired value that old rows still carry; it asks the same agent follow-up.
    'Agents': 'leadSource.realEstateAgent',
    'Internet': 'leadSource.internet',
    // Shown to borrowers as "Personal Referral".
    'Past Clients': 'leadSource.pastClients',
    'Other': 'leadSource.other',
  };

  static readonly DEFAULT_FIELDS: { name: string; displayName: string; isRequired: boolean }[] = [
    { name: LeadSourceFields.ANSWER, displayName: 'How did you hear about us?', isRequired: true },
    { name: LeadSourceFields.AGENT_FROM_LIST, displayName: 'Agent from the list', isRequired: false },
    { name: 'leadSource.socialMedia', displayName: 'Social Media follow-up', isRequired: true },
    { name: 'leadSource.realEstateAgent', displayName: 'Agent name follow-up', isRequired: true },
    { name: 'leadSource.internet', displayName: 'Internet follow-up', isRequired: true },
    { name: 'leadSource.pastClients', displayName: 'Personal Referral follow-up', isRequired: true },
    { name: 'leadSource.other', displayName: 'Other follow-up', isRequired: true },
  ];

  /** The fieldConfig key of a lead source's follow-up question, or null when it has no setting. */
  static followUpFieldFor = (leadSourceType: string): string | null =>
    LeadSourceFields.FOLLOW_UP_BY_LEAD_SOURCE[leadSourceType] ?? null;

  static hasFollowUpSetting = (field: string): boolean =>
    Object.values(LeadSourceFields.FOLLOW_UP_BY_LEAD_SOURCE).includes(field);

  /**
   * Falls back to the field's default rather than WizardStepBase.isRequired's blanket "required",
   * because the agent list defaults to optional.
   */
  static isRequired = (fieldConfig: RequiredSettings, field: string): boolean =>
    fieldConfig?.[field]?.isRequired ??
    LeadSourceFields.DEFAULT_FIELDS.find((d) => d.name === field)?.isRequired ??
    true;

  /** Free-text follow-ups with no setting (unknown or retired values) stay required, as before. */
  static isFollowUpRequired = (fieldConfig: RequiredSettings, leadSourceType: string): boolean => {
    const field = LeadSourceFields.followUpFieldFor(leadSourceType);
    return field ? LeadSourceFields.isRequired(fieldConfig, field) : true;
  };
}
